<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import moment from 'moment'
import { router } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import { getFormColor, getColleagueColor, FORM_TYPE_LABELS } from '../../../composables/useReservationsCalendar.js'
import { getInitials } from '../../../../../helpers.js'
import ReservationEventDrawer from './ReservationEventDrawer.vue'

const props = defineProps({
    days:               { type: Array,   default: () => [] },
    selectedMonth:      { type: Object },          // moment
    reservations:       { type: Array,   default: () => [] },
    reservationForms:   { type: Array,   default: () => [] },
    allEmployees:       { type: Array,   default: () => [] },
    visibleFormIds:     { type: Set,     default: () => new Set() },
    visibleColleagueIds:{ type: Set,     default: () => new Set() },
    showAlways:         { type: Boolean, default: false },
})

const emit = defineEmits([
    'prev-month', 'next-month', 'select-month',
    'toggle-form', 'toggle-colleague',
])

// ─── Stav ────────────────────────────────────────────────────────
const currentView = ref('mesic')          // mesic | den | agenda
const selectedDay = ref(moment())         // vybraný den
const openSheet   = ref(null)             // null | 'filters' | 'new' | 'account' | reservationObj
const drawerReservation = ref(null)       // pro desktop-style drawer na mobilu

// ─── Month picker ─────────────────────────────────────────────────
const monthPickerOpen = ref(false)
const pickerYear      = ref(moment().year())

function openMonthPicker() {
    pickerYear.value = props.selectedMonth?.year() ?? moment().year()
    monthPickerOpen.value = true
}

function selectPickerMonth(monthIndex) {
    const date = moment().year(pickerYear.value).month(monthIndex).date(1)
    emit('select-month', date)
    monthPickerOpen.value = false
}

const TODAY_STR = moment().format('YYYY-MM-DD')

const DOW_SHORT  = ['mon','tue','wed','thu','fri','sat','sun'].map(k => trans(`common.dow_short.${k}`))
const DOW_LONG   = ['sun','mon','tue','wed','thu','fri','sat'].map(k => trans(`common.dow_long.${k}`))
const MONTHS_GEN = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'].map(k => trans(`common.months_gen.${k}`))
const MONTHS_CZ  = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'].map(k => trans(`common.months.${k}`))

// ─── Title toolbar ───────────────────────────────────────────────
const toolbarTitle = computed(() => {
    if (currentView.value === 'agenda') return trans('reservations.cal_agenda')
    if (!props.selectedMonth) return ''
    return MONTHS_CZ[props.selectedMonth.month()] + ' ' + props.selectedMonth.year()
})

// ─── Měsíční mřížka ──────────────────────────────────────────────
const weeks = computed(() => {
    const result = []
    for (let i = 0; i < props.days.length; i += 7) result.push(props.days.slice(i, i + 7))
    return result
})

function dayDots(day) {
    if (!day?.reservations) return []
    const colors = [...new Set(day.reservations.map(r => getFormColor(r.reservation_forms)))]
    return colors.slice(0, 3)
}
function hasMultiday(day) {
    return day?.reservations?.some(r => r.date_start && r.date_end) ?? false
}
function multidayColor(day) {
    const r = day?.reservations?.find(r => r.date_start && r.date_end)
    return r ? getFormColor(r.reservation_forms) : null
}

function isSelectedDay(day) {
    return day && props.selectedMonth
        && day.day === selectedDay.value.date()
        && props.selectedMonth.month() === selectedDay.value.month()
        && props.selectedMonth.year()  === selectedDay.value.year()
}

function isToday(day) {
    if (!day || !props.selectedMonth) return false
    return day.day === moment().date()
        && props.selectedMonth.month() === moment().month()
        && props.selectedMonth.year()  === moment().year()
}

function selectDay(day) {
    if (!day || !props.selectedMonth) return
    selectedDay.value = props.selectedMonth.clone().date(day.day)
}

// ─── Události pro vybraný den ────────────────────────────────────
const dayReservations = computed(() => {
    const dateStr = selectedDay.value.format('YYYY-MM-DD')
    return props.reservations.filter(r => {
        if (!props.visibleFormIds.has(r.id_reservation_forms)) return false
        if (r.date === dateStr) return true
        if (r.date_start && r.date_end) return dateStr >= r.date_start && dateStr <= r.date_end
        return false
    }).sort((a, b) => (a.time_start ?? '').localeCompare(b.time_start ?? ''))
})

const selectedDayLabel = computed(() => {
    const dow   = DOW_LONG[selectedDay.value.day()]
    const day   = selectedDay.value.date()
    const month = MONTHS_GEN[selectedDay.value.month()]
    return `${dow} ${day}. ${month}`
})

// ─── Denní pohled: týdenní strip + timeline ──────────────────────
const M_HOUR_PX  = 56
const M_DAY_START = 7
const M_DAY_END   = 20
const M_HOURS     = M_DAY_END - M_DAY_START

const weekStrip = computed(() => {
    const dow = selectedDay.value.isoWeekday() - 1  // 0 = Po
    const monday = selectedDay.value.clone().subtract(dow, 'days')
    return Array.from({ length: 7 }, (_, i) => {
        const d = monday.clone().add(i, 'days')
        const dateStr = d.format('YYYY-MM-DD')
        const hasEvents = props.reservations.some(r => {
            if (!props.visibleFormIds.has(r.id_reservation_forms)) return false
            if (r.date === dateStr) return true
            if (r.date_start && r.date_end) return dateStr >= r.date_start && dateStr <= r.date_end
            return false
        })
        return { date: d, dateStr, dow: i, isToday: dateStr === TODAY_STR, hasEvents }
    })
})

const timedReservations = computed(() => {
    const dateStr = selectedDay.value.format('YYYY-MM-DD')
    return props.reservations.filter(r =>
        props.visibleFormIds.has(r.id_reservation_forms)
        && r.date === dateStr && r.time_start
    ).sort((a, b) => a.time_start.localeCompare(b.time_start))
})

const allDayReservations = computed(() => {
    const dateStr = selectedDay.value.format('YYYY-MM-DD')
    return props.reservations.filter(r =>
        props.visibleFormIds.has(r.id_reservation_forms)
        && r.date_start && r.date_end
        && dateStr >= r.date_start && dateStr <= r.date_end
    )
})

function mParse(t) {
    const [h, m] = (t ?? '00:00').split(':').map(Number)
    return h + (m || 0) / 60
}
function evtTop(t)     { return Math.max(0, mParse(t) - M_DAY_START) * M_HOUR_PX }
function evtHeight(s, e) { return Math.max(30, (mParse(e) - mParse(s)) * M_HOUR_PX - 3) }
function fmtTime(t) { return t ? t.substring(0, 5).replace(/^0/, '') : '' }

const nowTop = computed(() => {
    const now = new Date()
    const mins = now.getHours() + now.getMinutes() / 60
    return (mins - M_DAY_START) * M_HOUR_PX
})
const nowLabel = computed(() => {
    const now = new Date()
    return `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`
})
const isSelectedDayToday = computed(() => selectedDay.value.format('YYYY-MM-DD') === TODAY_STR)

// ─── Agenda ───────────────────────────────────────────────────────
const agendaGroups = computed(() => {
    const monthStr = props.selectedMonth?.format('YYYY-MM') ?? moment().format('YYYY-MM')
    const byDate = {}
    props.reservations.forEach(r => {
        if (!props.visibleFormIds.has(r.id_reservation_forms)) return
        const dateStr = r.date ?? r.date_start
        if (!dateStr?.startsWith(monthStr)) return
        ;(byDate[dateStr] ??= []).push(r)
    })
    return Object.keys(byDate).sort().map(dateStr => ({
        dateStr,
        date: moment(dateStr),
        reservations: byDate[dateStr].sort((a, b) => (a.time_start ?? '').localeCompare(b.time_start ?? '')),
    }))
})

// ─── Helpers ─────────────────────────────────────────────────────
function statusLabel(state) {
    return {
        accepted: trans('reservations.state_accepted_short'),
        pending: trans('reservations.state_pending_short'),
        rejected: trans('reservations.state_rejected_short'),
    }[state] ?? ''
}
function statusClass(state) {
    return {
        accepted: 'bg-emerald-100 text-emerald-700',
        pending:  'bg-amber-100 text-amber-700',
        rejected: 'bg-neutral-100 text-neutral-500',
    }[state] ?? ''
}

function totalPrice(r) {
    if (!r.bill_items?.length) return null
    const sum = r.bill_items.reduce((s, i) => s + i.unit_price_with_vat * i.amount, 0)
    return new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0 }).format(sum) + ' Kč'
}

// ─── Sheets ──────────────────────────────────────────────────────
function openReservation(r) {
    drawerReservation.value = r
}

// Scroll to today in day view
const timelineRef = ref(null)
watch(currentView, async (v) => {
    if (v === 'den') {
        await nextTick()
        if (timelineRef.value) {
            const scrollTo = Math.max(0, nowTop.value - 120)
            timelineRef.value.scrollTop = scrollTo
        }
    }
})

onMounted(() => {
    // Scroll to today if in month view
    const todayEl = document.getElementById('m-today-' + TODAY_STR)
    if (todayEl) todayEl.scrollIntoView({ block: 'center', behavior: 'auto' })
})
</script>

<template>
    <div
        class="flex flex-col bg-neutral-50 dark:bg-neutral-950 overflow-hidden"
        :class="showAlways ? 'h-dvh' : 'h-dvh lg:hidden'"
    >
        <!-- ── Header ─────────────────────────────────────────── -->
        <div class="bg-white dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800 flex-shrink-0">
            <!-- View pills -->
            <div class="flex gap-1 px-4 py-2">
                <button
                    v-for="[key, label] in [['mesic',$t('reservations.cal_month')],['den',$t('reservations.cal_day')],['agenda',$t('reservations.cal_agenda')]]"
                    :key="key"
                    class="px-3 py-1 rounded-full text-[13px] font-medium transition-colors"
                    :class="currentView === key
                        ? 'bg-blue-600 text-white'
                        : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'"
                    @click="currentView = key"
                >
                    {{ label }}
                </button>
            </div>
        </div>

        <!-- ── Obsah ──────────────────────────────────────────── -->
        <div class="flex-1 min-h-0 overflow-y-auto">

            <!-- ── MĚSÍC ────────────────────────────────────── -->
            <template v-if="currentView === 'mesic'">
                <!-- Mřížka -->
                <div class="bg-white dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800">
                    <!-- DOW headers -->
                    <div class="grid grid-cols-7 border-b border-neutral-50 dark:border-neutral-800">
                        <div
                            v-for="(d, i) in DOW_SHORT" :key="d"
                            class="text-center py-1.5 text-[10px] font-semibold uppercase tracking-wider"
                            :class="i >= 5 ? 'text-neutral-300 dark:text-neutral-600' : 'text-neutral-400 dark:text-neutral-500'"
                        >{{ d }}</div>
                    </div>

                    <!-- Weeks -->
                    <div v-for="(week, wi) in weeks" :key="wi" class="grid grid-cols-7">
                        <button
                            v-for="(day, di) in week"
                            :key="di"
                            class="flex flex-col items-center py-1.5 relative"
                            :class="{ 'opacity-30': !day }"
                            @click="day && selectDay(day)"
                        >
                            <!-- Day number -->
                            <div
                                class="w-8 h-8 flex items-center justify-center rounded-full text-[14px] font-medium"
                                :class="{
                                    'bg-blue-600 text-white font-semibold': isSelectedDay(day) && !isToday(day),
                                    'bg-blue-600 text-white font-semibold ring-2 ring-blue-200': isSelectedDay(day) && isToday(day),
                                    'text-blue-600 font-semibold': isToday(day) && !isSelectedDay(day),
                                    'text-neutral-700 dark:text-neutral-300': day && !isToday(day) && !isSelectedDay(day),
                                }"
                            >
                                {{ day?.day ?? '' }}
                            </div>

                            <!-- Multi-day bar -->
                            <div
                                v-if="day && hasMultiday(day)"
                                class="absolute bottom-0.5 left-0.5 right-0.5 h-[3px] rounded-full"
                                :style="{ background: multidayColor(day) }"
                            />

                            <!-- Colored dots -->
                            <div v-else-if="day" class="flex gap-px mt-0.5 h-[4px]">
                                <div
                                    v-for="(color, ci) in dayDots(day)" :key="ci"
                                    class="w-[4px] h-[4px] rounded-full"
                                    :style="{ background: color }"
                                />
                            </div>
                            <div v-else class="h-[4px]"/>
                        </button>
                    </div>
                </div>

                <!-- Selected day events -->
                <div class="px-4 pt-4 pb-2">
                    <div class="flex items-baseline justify-between mb-3">
                        <h3 class="text-[14px] font-semibold text-neutral-800 dark:text-neutral-100">{{ selectedDayLabel }}</h3>
                        <span class="text-[12px] text-neutral-400 dark:text-neutral-500">
                            {{ dayReservations.length }}
                            {{ $t('reservations.cal_res_suffix') }}
                        </span>
                    </div>

                    <!-- Event cards -->
                    <div v-if="dayReservations.length" class="flex flex-col gap-2">
                        <div
                            v-for="r in dayReservations" :key="r.id"
                            class="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-100 dark:border-neutral-700 flex overflow-hidden shadow-sm active:scale-[.98] transition-transform"
                            @click="openReservation(r)"
                        >
                            <!-- Left stripe -->
                            <div class="w-1 flex-shrink-0" :style="{ background: getFormColor(r.reservation_forms) }"/>

                            <!-- Time -->
                            <div class="w-16 flex-shrink-0 flex flex-col items-center justify-center py-3 border-r border-neutral-50 dark:border-neutral-700">
                                <template v-if="r.time_start">
                                    <span class="text-[12px] font-semibold text-neutral-700 dark:text-neutral-200 tabular-nums">{{ fmtTime(r.time_start) }}</span>
                                    <span class="text-[10px] text-neutral-400 dark:text-neutral-500 tabular-nums">{{ fmtTime(r.time_end) }}</span>
                                </template>
                                <span v-else class="text-[10px] text-neutral-400 dark:text-neutral-500 text-center px-1 leading-tight">{{ $t('reservations.cal_all_day') }}</span>
                            </div>

                            <!-- Main -->
                            <div class="flex-1 min-w-0 px-3 py-3">
                                <div
                                    class="text-[14px] font-medium text-neutral-900 dark:text-neutral-100 truncate"
                                    :class="r.state === 'rejected' ? 'line-through text-neutral-400' : ''"
                                >{{ r.name }}</div>
                                <div class="text-[11px] text-neutral-400 dark:text-neutral-500 mt-0.5 flex items-center gap-1">
                                    <span class="w-2 h-2 rounded-sm flex-shrink-0" :style="{ background: getFormColor(r.reservation_forms) }"/>
                                    {{ r.reservation_forms?.name }}
                                </div>
                            </div>

                            <!-- Right: status + colleague -->
                            <div class="flex flex-col items-end justify-center gap-1.5 px-3 py-3 flex-shrink-0">
                                <span class="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" :class="statusClass(r.state)">
                                    {{ statusLabel(r.state) }}
                                </span>
                                <div
                                    v-if="r.workers"
                                    class="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold text-white"
                                    :style="{ background: getColleagueColor(allEmployees.findIndex(e => e.id === r.id_reservation_workers)) }"
                                >
                                    {{ getInitials(r.workers.name) }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Empty state -->
                    <div v-else class="flex flex-col items-center py-12 text-neutral-300 dark:text-neutral-600">
                        <i class="pi pi-calendar !text-4xl mb-3"/>
                        <p class="text-sm">{{ $t('reservations.calendar_empty_day') }}</p>
                        <button
                            class="mt-4 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold"
                            @click="openSheet = 'new'"
                        >
                            <i class="pi pi-plus !text-xs mr-1"/>{{ $t('reservations.btn_add_res') }}
                        </button>
                    </div>
                </div>
            </template>

            <!-- ── DEN ──────────────────────────────────────── -->
            <template v-if="currentView === 'den'">
                <!-- Horizontal week strip -->
                <div class="bg-white dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800 px-2 py-2 flex gap-1">
                    <button
                        v-for="d in weekStrip" :key="d.dow"
                        class="flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-xl transition-colors"
                        :class="{
                            'bg-blue-600': d.date.format('YYYY-MM-DD') === selectedDay.format('YYYY-MM-DD'),
                            'opacity-40': false,
                        }"
                        @click="selectedDay = d.date.clone()"
                    >
                        <span
                            class="text-[10px] font-semibold uppercase tracking-wide"
                            :class="{
                                'text-white': d.date.format('YYYY-MM-DD') === selectedDay.format('YYYY-MM-DD'),
                                'text-blue-600': d.isToday && d.date.format('YYYY-MM-DD') !== selectedDay.format('YYYY-MM-DD'),
                                'text-neutral-400 dark:text-neutral-500': !d.isToday && d.date.format('YYYY-MM-DD') !== selectedDay.format('YYYY-MM-DD'),
                            }"
                        >{{ DOW_SHORT[d.dow] }}</span>
                        <span
                            class="text-[15px] font-semibold leading-none"
                            :class="{
                                'text-white': d.date.format('YYYY-MM-DD') === selectedDay.format('YYYY-MM-DD'),
                                'text-blue-600 font-bold': d.isToday && d.date.format('YYYY-MM-DD') !== selectedDay.format('YYYY-MM-DD'),
                                'text-neutral-700 dark:text-neutral-300': !d.isToday && d.date.format('YYYY-MM-DD') !== selectedDay.format('YYYY-MM-DD'),
                            }"
                        >{{ d.date.date() }}</span>
                        <div class="h-1">
                            <div
                                v-if="d.hasEvents"
                                class="w-1 h-1 rounded-full"
                                :class="d.date.format('YYYY-MM-DD') === selectedDay.format('YYYY-MM-DD') ? 'bg-white' : 'bg-blue-400'"
                            />
                        </div>
                    </button>
                </div>

                <!-- All-day strip -->
                <div
                    v-if="allDayReservations.length"
                    class="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-100 dark:border-neutral-700 px-4 py-2 flex flex-wrap gap-1.5"
                >
                    <span class="text-[10px] text-neutral-400 dark:text-neutral-500 font-medium self-center mr-1">{{ $t('reservations.cal_all_day') }}</span>
                    <div
                        v-for="r in allDayReservations" :key="r.id"
                        class="px-2.5 py-1 rounded-full text-white text-[11px] font-medium"
                        :style="{ background: getFormColor(r.reservation_forms) }"
                        @click="openReservation(r)"
                    >
                        {{ r.name }}
                    </div>
                </div>

                <!-- Timeline -->
                <div ref="timelineRef" class="overflow-y-auto pb-24">
                    <div class="relative" :style="{ height: M_HOURS * M_HOUR_PX + 28 + 'px' }">
                        <!-- Hour lines -->
                        <div
                            v-for="h in M_HOURS + 1" :key="h"
                            class="absolute left-0 right-0 flex items-start"
                            :style="{ top: (h - 1) * M_HOUR_PX + 'px', height: M_HOUR_PX + 'px' }"
                        >
                            <span class="w-14 text-right pr-3 text-[10px] text-neutral-400 dark:text-neutral-600 font-medium tabular-nums flex-shrink-0 -mt-2">
                                {{ M_DAY_START + h - 1 }}:00
                            </span>
                            <div class="flex-1 border-t border-neutral-100 dark:border-neutral-800"/>
                        </div>

                        <!-- Now indicator -->
                        <div
                            v-if="isSelectedDayToday"
                            class="absolute left-14 right-0 flex items-center z-10 pointer-events-none"
                            :style="{ top: nowTop + 'px' }"
                        >
                            <div class="w-2 h-2 rounded-full bg-red-500 -ml-1 flex-shrink-0"/>
                            <div class="flex-1 h-[1.5px] bg-red-500"/>
                            <span class="text-[10px] text-red-500 font-semibold ml-1 bg-white dark:bg-neutral-950 px-1 rounded">{{ nowLabel }}</span>
                        </div>

                        <!-- Events -->
                        <div class="absolute" style="left: 56px; right: 8px; top: 0; bottom: 0">
                            <div
                                v-for="r in timedReservations" :key="r.id"
                                class="absolute left-0 right-0 rounded-xl px-3 py-2 border-l-[3px] flex flex-col gap-0.5 cursor-pointer active:brightness-90"
                                :style="{
                                    top: evtTop(r.time_start) + 'px',
                                    height: evtHeight(r.time_start, r.time_end) + 'px',
                                    background: getFormColor(r.reservation_forms) + '18',
                                    borderLeftColor: getFormColor(r.reservation_forms),
                                }"
                                @click="openReservation(r)"
                            >
                                <span class="text-[10px] font-semibold tabular-nums" :style="{ color: getFormColor(r.reservation_forms) }">
                                    {{ fmtTime(r.time_start) }}–{{ fmtTime(r.time_end) }}
                                </span>
                                <span class="text-[12px] font-medium text-neutral-800 dark:text-neutral-200 truncate">{{ r.name }}</span>
                                <span v-if="r.state === 'pending'" class="text-[9px] font-bold uppercase tracking-wider text-amber-600">{{ $t('reservations.state_pending_label') }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <!-- ── AGENDA ────────────────────────────────────── -->
            <template v-if="currentView === 'agenda'">
                <div class="px-4 py-4 flex flex-col gap-0 pb-24">
                    <div
                        v-for="group in agendaGroups"
                        :key="group.dateStr"
                        class="flex gap-4 pb-5 mb-5 border-b border-neutral-100 dark:border-neutral-800 last:border-0"
                    >
                        <!-- Date column -->
                        <div class="w-12 flex-shrink-0 text-center">
                            <div
                                class="text-[28px] font-semibold leading-none tracking-tight"
                                :class="group.dateStr === TODAY_STR ? 'text-blue-600' : 'text-neutral-800 dark:text-neutral-100'"
                            >{{ group.date.date() }}</div>
                            <div class="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 mt-0.5">{{ DOW_SHORT[group.date.isoWeekday() - 1] }}</div>
                            <div v-if="group.dateStr === TODAY_STR" class="mt-1">
                                <span class="text-[9px] bg-blue-600 text-white rounded-full px-1.5 py-0.5 font-semibold">{{ $t('common.today').toLowerCase() }}</span>
                            </div>
                        </div>

                        <!-- Event cards -->
                        <div class="flex-1 min-w-0 flex flex-col gap-2">
                            <div
                                v-for="r in group.reservations" :key="r.id"
                                class="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-100 dark:border-neutral-700 flex overflow-hidden shadow-sm active:scale-[.98] transition-transform"
                                @click="openReservation(r)"
                            >
                                <div class="w-1 flex-shrink-0" :style="{ background: getFormColor(r.reservation_forms) }"/>
                                <div class="flex-1 min-w-0 px-3 py-3">
                                    <div class="flex items-start justify-between gap-2">
                                        <div class="min-w-0">
                                            <div class="text-[13px] font-medium text-neutral-900 dark:text-neutral-100 truncate" :class="r.state === 'rejected' ? 'line-through text-neutral-400' : ''">
                                                {{ r.name }}
                                            </div>
                                            <div class="text-[11px] text-neutral-400 dark:text-neutral-500 mt-0.5">
                                                <template v-if="r.time_start">{{ fmtTime(r.time_start) }} – {{ fmtTime(r.time_end) }}</template>
                                                <template v-else>{{ r.formatted_date }} – {{ r.formatted_date_end }}</template>
                                                · {{ r.reservation_forms?.name }}
                                            </div>
                                        </div>
                                        <span class="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5" :class="statusClass(r.state)">
                                            {{ statusLabel(r.state) }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="!agendaGroups.length" class="flex flex-col items-center py-16 text-neutral-300 dark:text-neutral-600">
                        <i class="pi pi-calendar !text-4xl mb-3"/>
                        <p class="text-sm">{{ $t('reservations.calendar_empty') }}</p>
                    </div>
                </div>
            </template>
        </div>

        <!-- ── FAB ───────────────────────────────────────────── -->
        <button
            class="fixed right-4 bottom-20 w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg flex items-center justify-center z-30 active:scale-95 transition-transform"
            @click="openSheet = 'new'"
        >
            <i class="pi pi-plus !text-xl"/>
        </button>

        <!-- ── Filters sheet ─────────────────────────────────── -->
        <Transition name="sheet">
            <div v-if="openSheet === 'filters'" class="fixed inset-0 z-40 flex flex-col justify-end">
                <div class="absolute inset-0 bg-black/30" @click="openSheet = null"/>
                <div class="relative bg-white dark:bg-neutral-900 rounded-t-3xl max-h-[85vh] flex flex-col">
                    <div class="w-12 h-1 bg-neutral-200 dark:bg-neutral-700 rounded-full mx-auto mt-3 mb-0 flex-shrink-0"/>

                    <div class="flex items-center justify-between px-5 py-4 flex-shrink-0">
                        <h3 class="text-[17px] font-semibold text-neutral-900 dark:text-white">{{ $t('common.show') }}</h3>
                        <button class="w-8 h-8 rounded-full flex items-center justify-center text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800" @click="openSheet = null">
                            <i class="pi pi-times !text-sm"/>
                        </button>
                    </div>

                    <div class="overflow-y-auto px-4 pb-8">
                        <!-- Forms -->
                        <div class="text-[11px] font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 px-1 mb-2">{{ $t('common.reservation_forms_label') }}</div>
                        <div
                            v-for="form in reservationForms" :key="form.id"
                            class="flex items-center gap-3 py-3 border-b border-neutral-50 dark:border-neutral-800 last:border-0"
                            :class="visibleFormIds.has(form.id) ? '' : 'opacity-50'"
                            @click="emit('toggle-form', form.id)"
                        >
                            <i
                                class="!text-[14px] w-5 flex-shrink-0"
                                :class="visibleFormIds.has(form.id) ? 'pi pi-eye' : 'pi pi-eye-slash text-neutral-300'"
                                :style="visibleFormIds.has(form.id) ? { color: getFormColor(form) } : {}"
                            />
                            <div class="w-3 h-3 rounded-sm flex-shrink-0" :style="{ background: getFormColor(form) }"/>
                            <div class="flex-1 min-w-0">
                                <div class="text-[14px] font-medium text-neutral-800 dark:text-neutral-200 truncate">{{ form.name }}</div>
                                <div class="text-[11px] text-neutral-400 dark:text-neutral-500">{{ FORM_TYPE_LABELS[form.form_type?.name] }}</div>
                            </div>
                        </div>

                        <!-- Colleagues -->
                        <div v-if="allEmployees.length" class="mt-4">
                            <div class="text-[11px] font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 px-1 mb-2">{{ $t('common.colleagues') }}</div>
                            <div
                                v-for="(emp, i) in allEmployees" :key="emp.id"
                                class="flex items-center gap-3 py-3 border-b border-neutral-50 dark:border-neutral-800 last:border-0"
                                :class="visibleColleagueIds.has(emp.id) ? '' : 'opacity-50'"
                                @click="emit('toggle-colleague', emp.id)"
                            >
                                <i
                                    class="!text-[14px] w-5 flex-shrink-0"
                                    :class="visibleColleagueIds.has(emp.id) ? 'pi pi-eye' : 'pi pi-eye-slash text-neutral-300'"
                                    :style="visibleColleagueIds.has(emp.id) ? { color: getColleagueColor(i) } : {}"
                                />
                                <div
                                    class="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-white flex-shrink-0"
                                    :style="{ background: getColleagueColor(i) }"
                                >{{ getInitials(emp.name) }}</div>
                                <div class="text-[14px] font-medium text-neutral-800 dark:text-neutral-200 truncate flex-1">{{ emp.name }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>

        <!-- ── New reservation sheet ─────────────────────────── -->
        <Transition name="sheet">
            <div v-if="openSheet === 'new'" class="fixed inset-0 z-40 flex flex-col justify-end">
                <div class="absolute inset-0 bg-black/30" @click="openSheet = null"/>
                <div class="relative bg-white dark:bg-neutral-900 rounded-t-3xl max-h-[80vh] flex flex-col">
                    <div class="w-12 h-1 bg-neutral-200 dark:bg-neutral-700 rounded-full mx-auto mt-3 flex-shrink-0"/>
                    <div class="flex items-start justify-between px-5 py-4 flex-shrink-0">
                        <div>
                            <h3 class="text-[17px] font-semibold text-neutral-900 dark:text-white">{{ $t('reservations.btn_new') }}</h3>
                            <p class="text-[12px] text-neutral-400 dark:text-neutral-500 mt-0.5">{{ $t('reservations.new_res_hint') }}</p>
                        </div>
                        <button class="w-8 h-8 rounded-full flex items-center justify-center text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 mt-1" @click="openSheet = null">
                            <i class="pi pi-times !text-sm"/>
                        </button>
                    </div>
                    <div class="overflow-y-auto px-3 pb-6">
                        <div
                            v-for="form in reservationForms" :key="form.id"
                            class="flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 dark:active:bg-neutral-700 transition-colors"
                            @click="router.visit('/w/' + form.slug)"
                        >
                            <div class="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0" :style="{ background: getFormColor(form) }">
                                <i class="pi pi-calendar !text-base"/>
                            </div>
                            <div class="flex-1 min-w-0">
                                <div class="text-[14px] font-semibold text-neutral-900 dark:text-neutral-100 truncate">{{ form.name }}</div>
                                <div class="text-[11px] text-neutral-400 dark:text-neutral-500">{{ FORM_TYPE_LABELS[form.form_type?.name] }}</div>
                            </div>
                            <i class="pi pi-chevron-right !text-xs text-neutral-300 dark:text-neutral-600"/>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>

        <!-- ── Account sheet ────────────────────────────────── -->
        <Transition name="sheet">
            <div v-if="openSheet === 'account'" class="fixed inset-0 z-40 flex flex-col justify-end">
                <div class="absolute inset-0 bg-black/30" @click="openSheet = null"/>
                <div class="relative bg-white dark:bg-neutral-900 rounded-t-3xl flex flex-col">
                    <div class="w-12 h-1 bg-neutral-200 dark:bg-neutral-700 rounded-full mx-auto mt-3 mb-0 flex-shrink-0"/>

                    <div class="flex items-center gap-3 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800">
                        <div class="w-10 h-10 rounded-full bg-neutral-800 dark:bg-neutral-600 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
                            {{ getInitials($page?.props?.user_initials ?? 'U') }}
                        </div>
                        <div>
                            <div class="text-[15px] font-semibold text-neutral-900 dark:text-white">{{ $page?.props?.user?.name }}</div>
                            <div class="text-[12px] text-neutral-400 dark:text-neutral-500">{{ $page?.props?.user?.email }}</div>
                        </div>
                    </div>

                    <div class="px-3 py-3 pb-8">
                        <button
                            class="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                            @click="router.visit('/app/settings'); openSheet = null"
                        >
                            <div class="w-9 h-9 rounded-xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0">
                                <i class="pi pi-cog text-neutral-600 dark:text-neutral-300 !text-base"/>
                            </div>
                            <span class="text-[15px] font-medium text-neutral-900 dark:text-neutral-100">{{ $t('common.settings') }}</span>
                        </button>
                        <button
                            class="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                            @click="router.visit('/logout'); openSheet = null"
                        >
                            <div class="w-9 h-9 rounded-xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0">
                                <i class="pi pi-sign-out text-neutral-600 dark:text-neutral-300 !text-base"/>
                            </div>
                            <span class="text-[15px] font-medium text-neutral-900 dark:text-neutral-100">{{ $t('common.nav_logout') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </Transition>

        <!-- ── Month picker sheet ───────────────────────────── -->
        <Transition name="sheet">
            <div v-if="monthPickerOpen" class="fixed inset-0 z-40 flex flex-col justify-end">
                <div class="absolute inset-0 bg-black/30" @click="monthPickerOpen = false"/>
                <div class="relative bg-white dark:bg-neutral-900 rounded-t-3xl pb-safe">
                    <div class="w-12 h-1 bg-neutral-200 dark:bg-neutral-700 rounded-full mx-auto mt-3 flex-shrink-0"/>

                    <!-- Year navigation -->
                    <div class="flex items-center justify-between px-6 py-4">
                        <button
                            class="w-9 h-9 flex items-center justify-center rounded-full text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                            @click="pickerYear--"
                        >
                            <i class="pi pi-chevron-left !text-sm"/>
                        </button>
                        <span class="text-[17px] font-semibold text-neutral-900 dark:text-white">{{ pickerYear }}</span>
                        <button
                            class="w-9 h-9 flex items-center justify-center rounded-full text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                            @click="pickerYear++"
                        >
                            <i class="pi pi-chevron-right !text-sm"/>
                        </button>
                    </div>

                    <!-- Month grid -->
                    <div class="grid grid-cols-3 gap-2 px-4 pb-8">
                        <button
                            v-for="(name, i) in MONTHS_CZ"
                            :key="i"
                            class="py-3 rounded-xl text-[14px] font-medium transition-colors"
                            :class="pickerYear === (props.selectedMonth?.year()) && i === props.selectedMonth?.month()
                                ? 'bg-blue-600 text-white'
                                : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'"
                            @click="selectPickerMonth(i)"
                        >
                            {{ name }}
                        </button>
                    </div>
                </div>
            </div>
        </Transition>

        <!-- ── Event detail drawer ───────────────────────────── -->
        <ReservationEventDrawer
            :reservation="drawerReservation"
            @close="drawerReservation = null"
            @status-changed="drawerReservation = null"
        />
    </div>
</template>

<style scoped>
.sheet-enter-active, .sheet-leave-active { transition: opacity .25s ease; }
.sheet-enter-active > div:last-child, .sheet-leave-active > div:last-child {
    transition: transform .28s cubic-bezier(.25,1,.3,1);
}
.sheet-enter-from { opacity: 0; }
.sheet-leave-to   { opacity: 0; }
.sheet-enter-from > div:last-child,
.sheet-leave-to > div:last-child {
    transform: translateY(100%);
}

.pt-safe { padding-top: env(safe-area-inset-top, 0px); }
.pb-safe { padding-bottom: env(safe-area-inset-bottom, 0px); }
</style>
